import { addDays, differenceInCalendarDays } from 'date-fns'
import { isEmpty } from 'lodash'
import {
  actionTypes,
  change,
  clearAsyncError,
  FormAction,
  getFormAsyncErrors,
  getFormMeta,
  getFormValues,
  stopAsyncValidation,
} from 'redux-form'
import * as Effects from 'redux-saga/effects'
import { IChangeReservationDate } from '../../../actions/bookingCalendar/reservationModal/changeReservationDate'
import { fireToastError } from '../../../actions/toasts/fireToast'
import { ICreateReservationFormValues } from '../../../containers/Form/CreateReservation/CreateReservation'
import i18n from '../../../i18n'
import { EventStatus } from '../../../models/Event'
import { ReservationModalActions } from '../../../reducers/bookingCalendar/actionNames'
import {
  getModalUpdateReservationIsOpen,
  getUpdateModalReservationId,
} from '../../../reducers/bookingCalendar/selectors/getModalOpen'
import { getRoomsAvailableWithinDateRangeWithoutEvent } from '../../../reducers/bookingCalendar/selectors/rooms'
import { getInvoiceReservationId } from '../../../reducers/invoice/getInvoice'
import { SynchronizeActions } from '../../../reducers/synchronize/actionNames'

function* changeDate(action: IChangeReservationDate) {
  const getForm = getFormValues(action.formName)
  const values: ICreateReservationFormValues = yield Effects.select(getForm)
  const meta = yield Effects.select(getFormMeta(action.formName))

  if (action.name === 'days' && meta.daysCount && meta.daysCount.active) {
    yield Effects.put(
      change(
        action.formName,
        'dateTo',
        addDays(values.dateFrom, Number(action.value)),
      ),
    )

    yield Effects.put(change(action.formName, 'dateFrom', values.dateFrom))
  }

  if (action.name === 'to' && values.dateFrom !== undefined) {
    yield Effects.put(
      change(
        action.formName,
        'daysCount',
        differenceInCalendarDays(action.value, values.dateFrom),
      ),
    )
  }
  if (action.name === 'from' && values.dateTo !== undefined) {
    yield Effects.put(
      change(
        action.formName,
        'daysCount',
        differenceInCalendarDays(values.dateTo, action.value),
      ),
    )
  }
}

function* afterFormChanged(action: FormAction) {
  if (
    ['dateFrom', 'dateTo', 'roomId', 'reservationStatus'].includes(
      action.meta.field,
    ) &&
    ['update_reservation', 'create_reservation'].includes(action.meta.form)
  ) {
    yield* validateDate()
  }

  return
}

function* validateDate() {
  const update = yield Effects.select(getModalUpdateReservationIsOpen)
  const formName = update ? 'update_reservation' : 'create_reservation'
  const values: ICreateReservationFormValues = yield Effects.select(
    getFormValues(formName),
  )

  if (!values) {
    return
  }
  if (values.reservationStatus === EventStatus.Canceled) {
    yield* clearErrors()
    return
  }

  const updateId = yield Effects.select(getUpdateModalReservationId)
  const reservationId = yield Effects.select(getInvoiceReservationId)
  const availableRooms = yield Effects.select(
    getRoomsAvailableWithinDateRangeWithoutEvent,
    {
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      editedEventId: update ? updateId : reservationId,
    },
  )

  if (values.roomId) {
    if (availableRooms.findIndex((r) => r.id === values.roomId) > -1) {
      yield* clearErrors()
    } else {
      yield* setErrors()
    }
  } else {
    if (isEmpty(availableRooms)) {
      yield* setErrors()
    } else {
      yield* clearErrors()
    }
  }
}

function* setErrors() {
  const update = yield Effects.select(getModalUpdateReservationIsOpen)
  const formName = update ? 'update_reservation' : 'create_reservation'
  const errors = yield Effects.select(getFormAsyncErrors(formName))
  const values: ICreateReservationFormValues = yield Effects.select(
    getFormValues(formName),
  )
  if (!(errors && errors.dateTo && errors.dateFrom)) {
    // if (errors && !(errors.dateTo && errors.dateFrom)) {
    yield Effects.put(
      stopAsyncValidation(
        update ? 'update_reservation' : 'create_reservation',
        {
          dateFrom: true,
          dateTo: true,
        },
      ),
    )
    const toastText =
      formName === 'create_reservation' && !values.roomId
        ? i18n.t('BBO_1438_no_room_available_with_selected_dates')
        : i18n.t('reservation_reserved_date_error')

    yield Effects.put(
      fireToastError(toastText, null, 'Event', 'reservationDateReservedError'),
    )
  }
}

function* clearErrors() {
  const update = yield Effects.select(getModalUpdateReservationIsOpen)
  const formName = update ? 'update_reservation' : 'create_reservation'
  yield Effects.put(clearAsyncError(formName, 'dateFrom'))
  yield Effects.put(clearAsyncError(formName, 'dateTo'))
}

export default function* () {
  yield Effects.takeLatest(actionTypes.CHANGE, afterFormChanged)
  yield Effects.takeLatest(SynchronizeActions.SyncCompleted, validateDate)
  yield Effects.debounce(200, ReservationModalActions.ChangeDate, changeDate)
}
